import * as React from 'react';
import { Box, Skeleton, Stack, Typography } from '@mui/material';
import { CustomerStats } from '../../../types/customers';
import { formatDollarAmount } from '../../../utils/global/helper';
import { HAIRLINE } from './colors';

interface Props {
  stats: CustomerStats | undefined;
  isLoading: boolean;
}

function formatRate(pct: number | null) {
  return pct === null ? '–' : `${pct.toFixed(0)}%`;
}

function Stat({
  label,
  value,
  flex,
  first,
}: {
  label: string;
  value: React.ReactNode;
  flex: number;
  first: boolean;
}) {
  return (
    <Box
      sx={{
        flex,
        px: 1,
        borderLeft: first ? 'none' : `1px solid ${HAIRLINE}`,
        textAlign: 'center',
      }}>
      <Typography variant="h4" align="center" sx={{ color: 'text.primary' }}>
        {value}
      </Typography>
      <Typography
        variant="caption"
        component="p"
        align="center"
        sx={{ color: 'text.secondary', mt: '4px' }}>
        {label}
      </Typography>
    </Box>
  );
}

function CustomerStatsStrip({ stats, isLoading }: Props) {
  if (isLoading || !stats) {
    return (
      <Stack
        direction="row"
        justifyContent="center"
        gap={3}
        sx={{ py: '14px', width: '100%' }}>
        {[0, 1, 2].map(key => (
          <Skeleton key={key} variant="text" width={64} height={40} />
        ))}
      </Stack>
    );
  }

  return (
    <Stack
      direction="row"
      alignItems="stretch"
      sx={{
        width: '100%',
        mt: '28px',
        py: '14px',
        borderTop: `1px solid ${HAIRLINE}`,
        borderBottom: `1px solid ${HAIRLINE}`,
      }}>
      <Stat
        label="No-shows"
        value={formatRate(stats.noShowRatePct)}
        flex={1}
        first
      />
      <Stat
        label="Cancellations"
        value={formatRate(stats.cancellationRatePct)}
        flex={1}
        first={false}
      />
      <Stat
        label="Lifetime value"
        value={formatDollarAmount(Math.round(stats.lifetimeValue / 100), true)}
        flex={1.2}
        first={false}
      />
    </Stack>
  );
}

export default CustomerStatsStrip;
